import { useEffect, useState, useCallback } from 'react'
import { useTrades } from '@/hooks/useTrades'
import TradeHistoryTable from '@/components/ui/TradeHistoryTable'
import {
  Activity, Bot, TrendingUp, TrendingDown, RefreshCw,
  DollarSign, BarChart2, Zap, AlertTriangle, Plus,
  X, Edit3, Target, Shield, Clock,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import PageHeader from '@/components/ui/PageHeader'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts'

const TYPE_COLORS: Record<string, string> = {
  behavioral_analysis: 'bg-purple/10 border-purple/20 text-purple-light',
  optimization:        'bg-blue/10   border-blue/20   text-blue-light',
  risk:                'bg-rose/10   border-rose/20   text-rose-light',
  market_analysis:     'bg-cyan/10   border-cyan/20   text-cyan',
  compliance:          'bg-amber/10  border-amber/20  text-amber',
  goals:               'bg-emerald/10 border-emerald/20 text-emerald',
}

function fmt(n: number, prefix = '\u00A3') {
  return `${prefix}${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
}

function fmtPct(n: number) {
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`
}

type Tab = 'positions' | 'trades' | 'pnl' | 'decisions'

export default function TradesPage() {
  const {
    decisions, executedTrades, stats, potState,
    positions, positionSummary, closedPositions, pnlHistory,
    fetchDecisions, fetchTrades, fetchExecutedTrades, fetchStats, fetchPotState,
    fetchPositions, fetchPositionSummary, fetchClosedPositions, fetchPnlHistory,
    closePosition, addPosition, updatePosition,
    loadingDecisions, loadingStats, loadingPot, loadingPositions,
  } = useTrades()

  const [activeTab, setActiveTab] = useState<Tab>('positions')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editSL, setEditSL] = useState('')
  const [editTP, setEditTP] = useState('')

  // Add position form state
  const [newTicker, setNewTicker] = useState('')
  const [newName, setNewName] = useState('')
  const [newAction, setNewAction] = useState<'LONG' | 'SHORT'>('LONG')
  const [newPrice, setNewPrice] = useState('')
  const [newQty, setNewQty] = useState('')
  const [newSL, setNewSL] = useState('')
  const [newTP, setNewTP] = useState('')
  const [newNotes, setNewNotes] = useState('')

  useEffect(() => {
    fetchTrades(30)
    fetchExecutedTrades()
    fetchStats()
    fetchPotState()
    fetchDecisions(20)
    fetchPositions()
    fetchPositionSummary()
    fetchClosedPositions()
    fetchPnlHistory()
  }, []) // eslint-disable-line

  const refreshAll = () => {
    fetchTrades(30)
    fetchExecutedTrades()
    fetchStats()
    fetchPotState()
    fetchDecisions(20)
    fetchPositions()
    fetchPositionSummary()
    fetchClosedPositions()
    fetchPnlHistory()
  }

  const handleClose = useCallback(async (id: number) => {
    if (!confirm('Close this position at current market price?')) return
    await closePosition(id)
  }, [closePosition])

  const handleAddPosition = useCallback(async () => {
    if (!newTicker || !newPrice || !newQty) return
    await addPosition({
      ticker: newTicker.toUpperCase(),
      name: newName || newTicker.toUpperCase(),
      action: newAction,
      entry_price: parseFloat(newPrice),
      quantity: parseFloat(newQty),
      stop_loss: newSL ? parseFloat(newSL) : undefined,
      take_profit: newTP ? parseFloat(newTP) : undefined,
      notes: newNotes,
    })
    setShowAddModal(false)
    setNewTicker(''); setNewName(''); setNewPrice(''); setNewQty('')
    setNewSL(''); setNewTP(''); setNewNotes('')
  }, [addPosition, newTicker, newName, newAction, newPrice, newQty, newSL, newTP, newNotes])

  const handleUpdatePosition = useCallback(async (id: number) => {
    await updatePosition(id, {
      stop_loss: editSL ? parseFloat(editSL) : undefined,
      take_profit: editTP ? parseFloat(editTP) : undefined,
    })
    setEditingId(null)
    setEditSL(''); setEditTP('')
  }, [updatePosition, editSL, editTP])

  const positive = (potState?.daily_pnl ?? 0) >= 0
  const totalPositive = (potState?.total_pnl ?? 0) >= 0
  const totalUnrealizedPositive = (positionSummary?.total_unrealized_pnl ?? 0) >= 0

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'positions', label: 'Positions', count: positions.length },
    { id: 'trades', label: 'Trades', count: executedTrades.length },
    { id: 'pnl', label: 'P&L Charts' },
    { id: 'decisions', label: 'Decisions', count: decisions.length },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Trades & Positions"
        subtitle="Position management, P&L tracking, and execution history"
        actions={
          <>
            <span className="badge badge-amber text-xs flex items-center gap-1">
              <Zap size={10} aria-hidden />
              Paper Mode
            </span>
            <button onClick={refreshAll} className="btn-secondary gap-1.5 text-xs">
              <RefreshCw size={13} aria-hidden /> Refresh
            </button>
          </>
        }
      />

      {/* Stats row — only meaningful once trades are executed */}
      {stats && stats.total_trades > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
          {/* Pot value */}
          <div className="stat-card">
            <p className="stat-label">Pot Value</p>
            <p className="stat-hero text-text-primary">{potState ? fmt(potState.pot_value) : '--'}</p>
            {potState && (
              <div className={positive ? 'stat-change-pos' : 'stat-change-neg'}>
                {positive ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}
                <span>{fmt(potState.daily_pnl)} today</span>
              </div>
            )}
          </div>

          {/* Unrealized P&L */}
          <div className="stat-card">
            <p className="stat-label">Unrealized P&L</p>
            <p className={clsx('stat-value text-2xl', totalUnrealizedPositive ? 'text-emerald' : 'text-rose-light')}>
              {positionSummary ? fmt(positionSummary.total_unrealized_pnl) : '--'}
            </p>
            <p className="text-xs text-text-muted">{positionSummary?.position_count ?? 0} open positions</p>
          </div>

          {/* Win rate */}
          <div className="stat-card">
            <p className="stat-label">Win Rate</p>
            <p className="stat-value text-2xl">{`${(stats.win_rate * 100).toFixed(0)}%`}</p>
            <p className="text-xs text-text-muted">
              {`Avg W: ${fmt(stats.avg_winner)} / L: ${fmt(stats.avg_loser)}`}
            </p>
          </div>

          {/* Best / worst position */}
          <div className="stat-card">
            <p className="stat-label">Best Position</p>
            <p className="stat-value text-2xl text-emerald">
              {positionSummary?.best_position ? `${positionSummary.best_position.ticker} ${fmtPct(positionSummary.best_position.pnl_pct)}` : '--'}
            </p>
            <p className="text-xs text-text-muted">
              Worst: {positionSummary?.worst_position ? `${positionSummary.worst_position.ticker} ${fmtPct(positionSummary.worst_position.pnl_pct)}` : '--'}
            </p>
          </div>

          {/* Exposure */}
          <div className="stat-card">
            <p className="stat-label">Total Exposure</p>
            <p className="stat-value text-2xl">{positionSummary ? fmt(positionSummary.total_exposure) : '--'}</p>
            <p className="text-xs text-text-muted">
              {positionSummary ? `${positionSummary.long_count}L / ${positionSummary.short_count}S` : '--'}
            </p>
          </div>
        </div>
      ) : (
        <div className="stat-card text-center py-8">
          <p className="text-text-muted text-lg">No trades executed yet</p>
          <p className="text-xs text-text-muted mt-2">
            Stats will appear once the AI agents execute their first paper trade.
            The £5k discretionary pot is ready — approve a recommendation to get started.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              activeTab === tab.id
                ? 'border-blue text-blue-light'
                : 'border-transparent text-text-muted hover:text-text-secondary',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 text-xs opacity-60">({tab.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Positions Tab ─────────────────────────────────────────────── */}
      {activeTab === 'positions' && (
        <div className="space-y-5">
          {/* Add position button */}
          <div className="flex justify-end">
            <button onClick={() => setShowAddModal(true)} className="btn-primary gap-1.5 text-xs">
              <Plus size={13} aria-hidden /> Add Position
            </button>
          </div>

          {/* Open positions table */}
          {loadingPositions && positions.length === 0 ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-12 skeleton" />
              ))}
            </div>
          ) : positions.length === 0 ? (
            <div className="card p-8 text-center text-text-muted text-sm">
              <Target size={24} className="mx-auto mb-2 opacity-30" aria-hidden />
              No open positions. Add a position to start tracking.
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Target size={15} className="text-text-muted" aria-hidden />
                <h2 className="font-semibold text-text-primary">Open Positions</h2>
                <span className="badge badge-blue ml-auto">{positions.length} open</span>
              </div>
              <div className="overflow-x-auto">
                <table className="table-pro w-full text-sm" aria-label="Open positions">
                  <thead>
                    <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                      <th className="py-2.5 px-4 text-left font-medium">Ticker</th>
                      <th className="py-2.5 px-4 text-left font-medium">Side</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Qty</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Entry</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Current</th>
                      <th className="py-2.5 px-4 !text-right font-medium">P&L</th>
                      <th className="py-2.5 px-4 !text-right font-medium">P&L %</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Weight</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Days</th>
                      <th className="py-2.5 px-4 !text-right font-medium">SL / TP</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map(pos => {
                      const pnlPos = pos.unrealized_pnl >= 0
                      const isEditing = editingId === pos.id
                      return (
                        <tr key={pos.id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-medium"><TickerLink ticker={pos.ticker} /></div>
                            <div className="text-xs text-text-muted truncate max-w-[120px]">{pos.name}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={clsx('badge text-xs', pos.action === 'LONG' ? 'badge-green' : 'badge-red')}>
                              {pos.action}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-text-primary">{pos.quantity}</td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted">{fmt(pos.entry_price)}</td>
                          <td className="py-3 px-4 text-right font-mono text-text-primary">{fmt(pos.current_price)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={pnlPos ? 'delta-chip-pos' : 'delta-chip-neg'}>
                              {pnlPos ? '+' : ''}{fmt(pos.unrealized_pnl)}
                            </span>
                          </td>
                          <td className={clsx('py-3 px-4 text-right font-mono text-xs', pnlPos ? 'text-emerald' : 'text-rose-light')}>
                            {fmtPct(pos.unrealized_pnl_pct)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted text-xs">
                            {pos.weight_pct.toFixed(1)}%
                          </td>
                          <td className="py-3 px-4 text-right text-text-muted text-xs">
                            <div className="flex items-center gap-1 justify-end">
                              <Clock size={10} aria-hidden />
                              {pos.holding_days}d
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-xs">
                            {isEditing ? (
                              <div className="flex flex-col gap-1 items-end">
                                <input
                                  type="number" step="0.01" placeholder="Stop Loss"
                                  value={editSL} onChange={e => setEditSL(e.target.value)}
                                  className="w-20 px-1.5 py-0.5 text-xs bg-surface-card border border-border rounded text-text-primary"
                                />
                                <input
                                  type="number" step="0.01" placeholder="Take Profit"
                                  value={editTP} onChange={e => setEditTP(e.target.value)}
                                  className="w-20 px-1.5 py-0.5 text-xs bg-surface-card border border-border rounded text-text-primary"
                                />
                                <div className="flex gap-1">
                                  <button onClick={() => handleUpdatePosition(pos.id)} className="text-emerald hover:underline text-[10px]">Save</button>
                                  <button onClick={() => setEditingId(null)} className="text-text-muted hover:underline text-[10px]">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div className="font-mono text-text-muted">
                                <div className="text-rose-light/70">SL: {pos.stop_loss ? fmt(pos.stop_loss) : '--'}</div>
                                <div className="text-emerald/70">TP: {pos.take_profit ? fmt(pos.take_profit) : '--'}</div>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center gap-1.5 justify-end">
                              <button
                                onClick={() => { setEditingId(pos.id); setEditSL(pos.stop_loss?.toString() ?? ''); setEditTP(pos.take_profit?.toString() ?? '') }}
                                className="p-1 rounded hover:bg-surface-hover text-text-muted hover:text-text-primary transition-colors"
                                title="Edit SL/TP"
                              >
                                <Edit3 size={12} />
                              </button>
                              <button
                                onClick={() => handleClose(pos.id)}
                                className="p-1 rounded hover:bg-rose/10 text-text-muted hover:text-rose-light transition-colors"
                                title="Close position"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Closed positions */}
          {closedPositions.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Shield size={15} className="text-text-muted" aria-hidden />
                <h2 className="font-semibold text-text-primary">Closed Positions</h2>
                <span className="badge badge-amber ml-auto">{closedPositions.length} closed</span>
              </div>
              <div className="overflow-x-auto">
                <table className="table-pro w-full text-sm" aria-label="Closed positions">
                  <thead>
                    <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                      <th className="py-2.5 px-4 text-left font-medium">Ticker</th>
                      <th className="py-2.5 px-4 text-left font-medium">Side</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Entry</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Exit</th>
                      <th className="py-2.5 px-4 !text-right font-medium">P&L</th>
                      <th className="py-2.5 px-4 !text-right font-medium">P&L %</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Closed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedPositions.map((cp, i) => {
                      const pnlPos = cp.realized_pnl >= 0
                      return (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                          <td className="py-3 px-4 font-medium"><TickerLink ticker={cp.ticker} /></td>
                          <td className="py-3 px-4">
                            <span className={clsx('badge text-xs', cp.action === 'LONG' ? 'badge-green' : 'badge-red')}>
                              {cp.action}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted">{fmt(cp.entry_price)}</td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted">{fmt(cp.exit_price)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={pnlPos ? 'delta-chip-pos' : 'delta-chip-neg'}>
                              {pnlPos ? '+' : ''}{fmt(cp.realized_pnl)}
                            </span>
                          </td>
                          <td className={clsx('py-3 px-4 text-right font-mono text-xs', pnlPos ? 'text-emerald' : 'text-rose-light')}>
                            {fmtPct(cp.realized_pnl_pct)}
                          </td>
                          <td className="py-3 px-4 text-right text-xs text-text-muted">
                            {cp.exit_date ? formatDistanceToNow(new Date(cp.exit_date), { addSuffix: true }) : '--'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Trades Tab ────────────────────────────────────────────────── */}
      {activeTab === 'trades' && (
        <div className="space-y-5">
          {/* Executed trades from pipeline */}
          {executedTrades.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Activity size={15} className="text-text-muted" aria-hidden />
                <h2 className="font-semibold text-text-primary">Pipeline Executed Trades</h2>
                <span className="badge badge-green ml-auto">{executedTrades.length} trades</span>
              </div>
              <div className="overflow-x-auto">
                <table className="table-pro w-full text-sm" aria-label="Executed trades">
                  <thead>
                    <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
                      <th className="py-2.5 px-4 text-left font-medium">Date</th>
                      <th className="py-2.5 px-4 text-left font-medium">Ticker</th>
                      <th className="py-2.5 px-4 text-left font-medium">Side</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Size</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Entry</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Exit</th>
                      <th className="py-2.5 px-4 !text-right font-medium">P&L</th>
                      <th className="py-2.5 px-4 !text-right font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executedTrades.map(t => {
                      const pnlPositive = (t.pnl_gbp ?? 0) >= 0
                      return (
                        <tr key={t.id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                          <td className="py-3 px-4 text-text-muted text-xs font-mono">
                            {format(new Date(t.created_at), 'dd MMM yy HH:mm')}
                          </td>
                          <td className="py-3 px-4 font-medium"><TickerLink ticker={t.ticker} /></td>
                          <td className="py-3 px-4">
                            <span className={clsx('badge text-xs', t.action === 'BUY' ? 'badge-green' : 'badge-red')}>
                              {t.action}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-text-primary">{fmt(t.size_gbp)}</td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted">{fmt(t.entry_price)}</td>
                          <td className="py-3 px-4 text-right font-mono text-text-muted">
                            {t.exit_price != null ? fmt(t.exit_price) : '--'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {t.pnl_gbp != null ? (
                              <span className={pnlPositive ? 'delta-chip-pos' : 'delta-chip-neg'}>
                                {pnlPositive ? '+' : ''}{fmt(t.pnl_gbp)}
                              </span>
                            ) : (
                              <span className="font-mono text-text-muted">--</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className={clsx('badge text-xs',
                              t.status === 'filled' ? 'badge-green' :
                              t.status === 'simulated' ? 'badge-amber' : 'badge-blue',
                            )}>
                              {t.paper ? 'Paper' : t.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Legacy trade history */}
          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <TrendingUp size={15} className="text-text-muted" aria-hidden />
              <h2 className="font-semibold text-text-primary">Trade History</h2>
            </div>
            <TradeHistoryTable limit={30} />
          </div>
        </div>
      )}

      {/* ── P&L Charts Tab ────────────────────────────────────────────── */}
      {activeTab === 'pnl' && (
        <div className="space-y-5">
          {/* Cumulative P&L line chart */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={15} className="text-text-muted" aria-hidden />
              <h2 className="font-semibold text-text-primary">Cumulative P&L</h2>
            </div>
            {pnlHistory.length === 0 ? (
              <div className="py-8 text-center text-text-muted text-sm">No P&L data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={pnlHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => { try { return format(new Date(d), 'dd MMM') } catch { return d } }}
                    tick={{ fill: '#999', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  />
                  <YAxis
                    tick={{ fill: '#999', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickFormatter={(v: number) => `${v >= 0 ? '' : '-'}$${Math.abs(v).toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12, color: 'rgb(var(--text-primary))'}}
                    labelStyle={{ color: '#aaa' }}
                    formatter={(val: number) => [`$${val.toFixed(2)}`, 'Cumulative P&L']}
                    labelFormatter={(d: string) => { try { return format(new Date(d), 'dd MMM yyyy') } catch { return d } }}
                  />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                  <Line
                    type="monotone" dataKey="cumulative_pnl" stroke="#22d3ee"
                    strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Daily P&L bar chart */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 size={15} className="text-text-muted" aria-hidden />
              <h2 className="font-semibold text-text-primary">Daily P&L</h2>
            </div>
            {pnlHistory.length === 0 ? (
              <div className="py-8 text-center text-text-muted text-sm">No P&L data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pnlHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => { try { return format(new Date(d), 'dd') } catch { return d } }}
                    tick={{ fill: '#999', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  />
                  <YAxis
                    tick={{ fill: '#999', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12, color: 'rgb(var(--text-primary))'}}
                    labelStyle={{ color: '#aaa' }}
                    formatter={(val: number) => [`$${val.toFixed(2)}`, 'Daily P&L']}
                    labelFormatter={(d: string) => { try { return format(new Date(d), 'dd MMM yyyy') } catch { return d } }}
                  />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                  <Bar
                    dataKey="daily_pnl" radius={[2, 2, 0, 0]}
                    fill="#22d3ee"
                    // Color bars green/red based on value
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    shape={(props: any) => {
                      const { x, y, width, height, payload } = props
                      const color = (payload?.daily_pnl ?? 0) >= 0 ? '#34d399' : '#f87171'
                      return <rect x={x} y={y} width={width} height={height} fill={color} rx={2} />
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Summary stats */}
          {positionSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="stat-card">
                <p className="stat-label">Closed Trades</p>
                <p className="stat-value">{positionSummary.closed_count}</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Realized P&L</p>
                <p className={clsx('stat-value', positionSummary.closed_pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
                  {fmt(positionSummary.closed_pnl)}
                </p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Winning / Losing</p>
                <p className="stat-value">{positionSummary.win_count}W / {positionSummary.loss_count}L</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Avg Holding</p>
                <p className="stat-value">{positionSummary.avg_holding_days} days</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Decisions Tab ─────────────────────────────────────────────── */}
      {activeTab === 'decisions' && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-5">
            <Bot size={15} className="text-text-muted" aria-hidden />
            <h2 className="font-semibold text-text-primary">Agent Decision Log</h2>
            <span className="badge badge-blue ml-auto">{decisions.length} decisions</span>
          </div>

          {loadingDecisions && decisions.length === 0 ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-12 skeleton" />
              ))}
            </div>
          ) : (
            <ul className="space-y-3" aria-label="Agent decision log">
              {decisions.map((d) => {
                const colorCls = TYPE_COLORS[d.agent_type] ?? 'bg-surface-card border-border text-text-muted'
                return (
                  <li key={d.id} className="flex items-start gap-3">
                    <div className={clsx('w-8 h-8 rounded-lg border flex items-center justify-center text-[10px] font-bold shrink-0', colorCls)}>
                      {d.agent_acronym}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary leading-snug">{d.decision}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-text-muted">
                          {formatDistanceToNow(new Date(d.timestamp), { addSuffix: true })}
                        </span>
                        <span className="text-xs text-text-muted">
                          Confidence: <span className="text-emerald font-medium">{(d.confidence * 100).toFixed(0)}%</span>
                        </span>
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs font-mono text-emerald">{(d.confidence * 100).toFixed(0)}%</span>
                      <div className="w-16 h-1.5 bg-surface-hover rounded-full">
                        <div
                          className="h-full bg-emerald rounded-full"
                          style={{ width: `${d.confidence * 100}%` }}
                        />
                      </div>
                    </div>
                  </li>
                )
              })}
              {decisions.length === 0 && !loadingDecisions && (
                <li className="py-8 text-center text-text-muted text-sm">
                  <Activity size={24} className="mx-auto mb-2 opacity-30" aria-hidden />
                  No decisions recorded yet. Run an analysis to populate the log.
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {/* ── Add Position Modal ────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="card p-6 w-full max-w-md mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Add Position</h3>
              <button onClick={() => setShowAddModal(false)} className="text-text-muted hover:text-text-primary" title="Close dialog" aria-label="Close dialog">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-muted block mb-1">Ticker *</label>
                <input
                  type="text" value={newTicker} onChange={e => setNewTicker(e.target.value)}
                  placeholder="AAPL" className="input"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Name</label>
                <input
                  type="text" value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="Apple Inc." className="input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-text-muted block mb-1">Side</label>
                <select
                  value={newAction} onChange={e => setNewAction(e.target.value as 'LONG' | 'SHORT')}
                  className="input"
                  aria-label="Position side"
                >
                  <option value="LONG">LONG</option>
                  <option value="SHORT">SHORT</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Entry Price *</label>
                <input
                  type="number" step="0.01" value={newPrice} onChange={e => setNewPrice(e.target.value)}
                  placeholder="0.00" className="input"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Quantity *</label>
                <input
                  type="number" step="1" value={newQty} onChange={e => setNewQty(e.target.value)}
                  placeholder="10" className="input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-muted block mb-1">Stop Loss</label>
                <input
                  type="number" step="0.01" value={newSL} onChange={e => setNewSL(e.target.value)}
                  placeholder="Optional" className="input"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Take Profit</label>
                <input
                  type="number" step="0.01" value={newTP} onChange={e => setNewTP(e.target.value)}
                  placeholder="Optional" className="input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-text-muted block mb-1">Notes</label>
              <input
                type="text" value={newNotes} onChange={e => setNewNotes(e.target.value)}
                placeholder="Trade thesis..." className="input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddModal(false)} className="btn-secondary text-xs">Cancel</button>
              <button
                onClick={handleAddPosition}
                disabled={!newTicker || !newPrice || !newQty}
                className="btn-primary text-xs disabled:opacity-40"
              >
                Add Position
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
